import { useEffect, useState, useCallback } from 'react';
import { apiFetch, apiPut } from '../api/client';
import { PageContainer } from '../components/PageContainer';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PricingRow {
  id: string;
  productType: string;
  sizeLabel: string;
  orientation: string;
  printfulBaseCost: number;
  retailPrice: number;
  marginPct: number;
  active: boolean;
  updatedAt: string;
}

type EditField = 'retailPrice' | 'printfulBaseCost';

interface EditingCell {
  id: string;
  field: EditField;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PRODUCT_TYPES = ['matte_paper', 'photo_paper', 'canvas', 'thin_canvas'] as const;
type ProductType = (typeof PRODUCT_TYPES)[number];

const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  matte_paper: 'Matte Paper',
  photo_paper: 'Photo Paper',
  canvas:      'Canvas',
  thin_canvas: 'Thin Canvas',
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  page: {
    background: '#111118',
    minHeight: '100vh',
    padding: '32px',
    fontFamily: 'system-ui, sans-serif',
    color: '#e8eaf6',
  } as React.CSSProperties,

  heading: {
    fontSize: '22px',
    fontWeight: 600,
    color: '#ffffff',
    marginBottom: '4px',
  } as React.CSSProperties,

  subheading: {
    fontSize: '13px',
    color: '#666980',
    marginBottom: '28px',
  } as React.CSSProperties,

  tabs: {
    display: 'flex',
    gap: '4px',
    marginBottom: '24px',
    borderBottom: '1px solid #1e1e2e',
    paddingBottom: '0',
  } as React.CSSProperties,

  tab: (active: boolean): React.CSSProperties => ({
    padding: '8px 18px',
    fontSize: '13px',
    fontWeight: 500,
    border: 'none',
    borderBottom: active ? '2px solid #a0a8ff' : '2px solid transparent',
    background: 'none',
    color: active ? '#a0a8ff' : '#666980',
    cursor: 'pointer',
    transition: 'color 0.15s',
    marginBottom: '-1px',
  }),

  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '13px',
  } as React.CSSProperties,

  th: {
    textAlign: 'left' as const,
    padding: '8px 12px',
    color: '#666980',
    fontWeight: 500,
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: '1px solid #1e1e2e',
  } as React.CSSProperties,

  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #16161f',
    verticalAlign: 'middle' as const,
  } as React.CSSProperties,

  editableCell: (editing: boolean): React.CSSProperties => ({
    padding: '10px 12px',
    borderBottom: '1px solid #16161f',
    verticalAlign: 'middle',
    cursor: editing ? 'default' : 'pointer',
  }),

  priceInput: {
    background: '#1a1a2e',
    border: '1px solid #a0a8ff',
    borderRadius: '4px',
    color: '#ffffff',
    fontSize: '13px',
    padding: '4px 8px',
    width: '80px',
    outline: 'none',
  } as React.CSSProperties,

  priceDisplay: {
    color: '#e8eaf6',
    borderRadius: '4px',
    padding: '2px 4px',
  } as React.CSSProperties,

  marginGood: { color: '#6ee7b7' } as React.CSSProperties,
  marginWarn: { color: '#fbbf24' } as React.CSSProperties,
  marginBad:  { color: '#f87171' } as React.CSSProperties,

  toggle: (active: boolean): React.CSSProperties => ({
    width: '34px',
    height: '18px',
    background: active ? '#a0a8ff' : '#2a2a3e',
    borderRadius: '9px',
    border: 'none',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background 0.2s',
    flexShrink: 0,
  }),

  toggleKnob: (active: boolean): React.CSSProperties => ({
    position: 'absolute',
    top: '2px',
    left: active ? '18px' : '2px',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    background: '#ffffff',
    transition: 'left 0.2s',
  }),

  savingDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#a0a8ff',
    marginLeft: '6px',
    animation: 'pulse 1s infinite',
  } as React.CSSProperties,

  errorBanner: {
    background: '#2a1a1a',
    border: '1px solid #f87171',
    borderRadius: '6px',
    padding: '10px 14px',
    color: '#f87171',
    fontSize: '13px',
    marginBottom: '20px',
  } as React.CSSProperties,

  emptyState: {
    padding: '48px',
    textAlign: 'center' as const,
    color: '#666980',
    fontSize: '14px',
  } as React.CSSProperties,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function marginStyle(pct: number): React.CSSProperties {
  if (pct >= 0.3) return styles.marginGood;
  if (pct >= 0.15) return styles.marginWarn;
  return styles.marginBad;
}

function formatPct(pct: number): string {
  return `${(pct * 100).toFixed(1)}%`;
}

function parseSizeLabel(label: string): [number, number] {
  const [w, h] = label.split('x').map(Number);
  return [w ?? 0, h ?? 0];
}

function sortRows(rows: PricingRow[]): PricingRow[] {
  return [...rows].sort((a, b) => {
    const [aw, ah] = parseSizeLabel(a.sizeLabel);
    const [bw, bh] = parseSizeLabel(b.sizeLabel);
    if (aw !== bw) return aw - bw;
    return ah - bh;
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PricingPage() {
  const [rows, setRows]           = useState<PricingRow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ProductType>('matte_paper');
  const [editing, setEditing]     = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving]       = useState<Set<string>>(new Set());

  // ─── Load ───────────────────────────────────────────────────────────────

  useEffect(() => {
    setLoading(true);
    apiFetch<PricingRow[]>('/pricing')
      .then(setRows)
      .catch((e) => setError(e.message ?? 'Failed to load pricing data'))
      .finally(() => setLoading(false));
  }, []);

  // ─── Edit helpers ────────────────────────────────────────────────────────

  const startEdit = (id: string, field: EditField, currentValue: number) => {
    setEditing({ id, field });
    setEditValue(currentValue.toFixed(2));
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditValue('');
  };

  const commitEdit = useCallback(async () => {
    if (!editing) return;
    const parsed = parseFloat(editValue);
    if (isNaN(parsed) || parsed < 0) { cancelEdit(); return; }

    const row = rows.find((r) => r.id === editing.id);
    if (!row) { cancelEdit(); return; }

    // Optimistic update
    const newRetail = editing.field === 'retailPrice'      ? parsed : row.retailPrice;
    const newBase   = editing.field === 'printfulBaseCost' ? parsed : row.printfulBaseCost;
    const newMargin = newRetail > 0 ? (newRetail - newBase) / newRetail : 0;

    setRows((prev) =>
      prev.map((r) =>
        r.id === editing.id
          ? { ...r, [editing.field]: parsed, marginPct: newMargin }
          : r
      )
    );
    cancelEdit();

    setSaving((prev) => new Set(prev).add(editing.id));
    try {
      const updated = await apiPut<PricingRow>(`/pricing/${editing.id}`, {
        [editing.field]: parsed,
      });
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (e: any) {
      setError(e.message ?? 'Failed to save');
      // Revert optimistic update
      setRows((prev) => prev.map((r) => (r.id === row.id ? row : r)));
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(editing.id);
        return next;
      });
    }
  }, [editing, editValue, rows]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter')  commitEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  // ─── Toggle active ───────────────────────────────────────────────────────

  const toggleActive = async (row: PricingRow) => {
    const newActive = !row.active;
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, active: newActive } : r))
    );
    setSaving((prev) => new Set(prev).add(row.id));
    try {
      const updated = await apiPut<PricingRow>(`/pricing/${row.id}`, { active: newActive });
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (e: any) {
      setError(e.message ?? 'Failed to save');
      setRows((prev) => prev.map((r) => (r.id === row.id ? row : r)));
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  const tabRows = sortRows(rows.filter((r) => r.productType === activeTab));

  return (
    <PageContainer>
    <div style={styles.page}>
      <div style={styles.heading}>Pricing</div>
      <div style={styles.subheading}>
        Click any base cost or retail price to edit inline. Margin is computed automatically.
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* Tabs */}
      <div style={styles.tabs}>
        {PRODUCT_TYPES.map((pt) => (
          <button
            key={pt}
            style={styles.tab(activeTab === pt)}
            onClick={() => { setActiveTab(pt); cancelEdit(); }}
          >
            {PRODUCT_TYPE_LABELS[pt]}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div style={styles.emptyState}>Loading…</div>
      ) : tabRows.length === 0 ? (
        <div style={styles.emptyState}>No pricing rows found for this product type.</div>
      ) : (
        <table style={styles.table}>
          <thead>
          <tr>
            <th style={styles.th}>Size</th>
            <th style={styles.th}>Base Cost</th>
            <th style={styles.th}>Retail Price</th>
            <th style={styles.th}>Margin</th>
            <th style={styles.th}>Active</th>
          </tr>
          </thead>
          <tbody>
          {tabRows.map((row) => {
            const isSaving = saving.has(row.id);
            const isEditingBase   = editing?.id === row.id && editing.field === 'printfulBaseCost';
            const isEditingRetail = editing?.id === row.id && editing.field === 'retailPrice';

            // Live margin from current state (reflects in-progress edits)
            const liveMargin = row.marginPct;

            return (
              <tr
                key={row.id}
                style={{ opacity: row.active ? 1 : 0.45 }}
              >
                {/* Size */}
                <td style={styles.td}>
                  <span style={{ fontWeight: 500 }}>{row.sizeLabel}</span>
                  {isSaving && <span style={styles.savingDot} />}
                </td>

                {/* Base Cost */}
                <td
                  style={styles.editableCell(isEditingBase)}
                  onClick={() => !isEditingBase && startEdit(row.id, 'printfulBaseCost', row.printfulBaseCost)}
                >
                  {isEditingBase ? (
                    <input
                      autoFocus
                      style={styles.priceInput}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={commitEdit}
                      onKeyDown={handleKeyDown}
                    />
                  ) : (
                    <span style={{ ...styles.priceDisplay, color: '#888aaa' }}>
                        ${row.printfulBaseCost.toFixed(2)}
                      </span>
                  )}
                </td>

                {/* Retail Price */}
                <td
                  style={styles.editableCell(isEditingRetail)}
                  onClick={() => !isEditingRetail && startEdit(row.id, 'retailPrice', row.retailPrice)}
                >
                  {isEditingRetail ? (
                    <input
                      autoFocus
                      style={styles.priceInput}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={commitEdit}
                      onKeyDown={handleKeyDown}
                    />
                  ) : (
                    <span style={styles.priceDisplay}>
                        ${row.retailPrice.toFixed(2)}
                      </span>
                  )}
                </td>

                {/* Margin */}
                <td style={styles.td}>
                    <span style={marginStyle(liveMargin)}>
                      {formatPct(liveMargin)}
                    </span>
                </td>

                {/* Active toggle */}
                <td style={styles.td}>
                  <button
                    style={styles.toggle(row.active)}
                    onClick={() => toggleActive(row)}
                    title={row.active ? 'Disable' : 'Enable'}
                  >
                    <span style={styles.toggleKnob(row.active)} />
                  </button>
                </td>
              </tr>
            );
          })}
          </tbody>
        </table>
      )}
    </div>
    </PageContainer>
  );
}